package kz.dentalcrm.app.screens.protocols.detail

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_treatment_updated
import kotlin.random.Random
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.ViewEvent
import kz.dentalcrm.app.core.base.ViewSideEffect
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.data.network.response_model.protocol_templates.Option
import kz.dentalcrm.app.data.network.response_model.protocol_templates.TreatmentDetailResponseItem
import kz.dentalcrm.app.domain.TreatmentRepository
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

private fun generateId() = Random.nextLong().toString()

data class ProtocolOptionUi(
    val id: Int = 0,
    val localId: String = generateId(),
    val value: String = "",
    val isCustom: Int = 0,
    val error: String? = null
)

data class ProtocolQuestionUi(
    val id: Int = 0,
    val localId: String = generateId(),
    val name: String = "",
    val options: List<ProtocolOptionUi> = emptyList(),
    val error: String? = null
)

data class DetailProtocolState(
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val questions: List<ProtocolQuestionUi> = emptyList(),
    val protocolId: String? = null
)

sealed interface DetailProtocolEvent : ViewEvent {
    data class LoadData(val id: String) : DetailProtocolEvent
    data object AddQuestion : DetailProtocolEvent
    data class DeleteQuestion(val localId: String) : DetailProtocolEvent
    data class UpdateQuestionText(val localId: String, val text: String) : DetailProtocolEvent
    data class AddOption(val questionLocalId: String) : DetailProtocolEvent
    data class DeleteOption(val questionLocalId: String, val optionLocalId: String) : DetailProtocolEvent
    data class UpdateOptionText(val questionLocalId: String, val optionLocalId: String, val text: String) : DetailProtocolEvent
    data object Save : DetailProtocolEvent
    data object OnBack : DetailProtocolEvent
}

sealed interface DetailProtocolEffect : ViewSideEffect {
    data class ShowError(val message: String) : DetailProtocolEffect
    data object NavigateBack : DetailProtocolEffect
    data object ShowSuccess : DetailProtocolEffect
}

class DetailProtocolViewModel : BaseStateViewModel<DetailProtocolEvent, DetailProtocolState, DetailProtocolEffect>() {

    private val repository: TreatmentRepository by inject()

    override fun setInitialState(): DetailProtocolState = DetailProtocolState()

    override fun handleEvents(event: DetailProtocolEvent) {
        when (event) {
            is DetailProtocolEvent.LoadData -> loadData(event.id)
            is DetailProtocolEvent.AddQuestion -> addQuestion()
            is DetailProtocolEvent.DeleteQuestion -> deleteQuestion(event.localId)
            is DetailProtocolEvent.UpdateQuestionText -> updateQuestionText(event.localId, event.text)
            is DetailProtocolEvent.AddOption -> addOption(event.questionLocalId)
            is DetailProtocolEvent.DeleteOption -> deleteOption(event.questionLocalId, event.optionLocalId)
            is DetailProtocolEvent.UpdateOptionText -> updateOptionText(event.questionLocalId, event.optionLocalId, event.text)
            is DetailProtocolEvent.Save -> save()
            is DetailProtocolEvent.OnBack -> setEffect { DetailProtocolEffect.NavigateBack }
        }
    }

    private fun loadData(id: String) {
        setState { copy(isLoading = true, protocolId = id) }
        launch {
            when (val result = repository.getTreatmentDetail(id)) {
                is Result.Success -> {
                    val questions = result.value?.map { item ->
                        ProtocolQuestionUi(
                            id = item.id,
                            name = item.name,
                            options = item.options.map { option ->
                                ProtocolOptionUi(
                                    id = option.id,
                                    value = option.value,
                                    isCustom = option.is_custom
                                )
                            }
                        )
                    } ?: emptyList()
                    setState { copy(isLoading = false, questions = questions) }
                }
                is Result.Error -> {
                    setState { copy(isLoading = false) }
                    setEffect { DetailProtocolEffect.ShowError(result.message ?: "Error loading data") }
                }
                else -> {
                    setState { copy(isLoading = false) }
                    setEffect { DetailProtocolEffect.ShowError("Unknown error") }
                }
            }
        }
    }

    private fun addQuestion() {
        setState {
            copy(questions = questions + ProtocolQuestionUi())
        }
    }

    private fun deleteQuestion(localId: String) {
        setState {
            copy(questions = questions.filter { it.localId != localId })
        }
    }

    private fun updateQuestionText(localId: String, text: String) {
        setState {
            copy(questions = questions.map {
                if (it.localId == localId) it.copy(name = text, error = null) else it
            })
        }
    }

    private fun addOption(questionLocalId: String) {
        setState {
            copy(questions = questions.map { question ->
                if (question.localId == questionLocalId) {
                    question.copy(options = question.options + ProtocolOptionUi())
                } else {
                    question
                }
            })
        }
    }

    private fun deleteOption(questionLocalId: String, optionLocalId: String) {
        setState {
            copy(questions = questions.map { question ->
                if (question.localId == questionLocalId) {
                    question.copy(options = question.options.filter { it.localId != optionLocalId })
                } else {
                    question
                }
            })
        }
    }

    private fun updateOptionText(questionLocalId: String, optionLocalId: String, text: String) {
        setState {
            copy(questions = questions.map { question ->
                if (question.localId == questionLocalId) {
                    question.copy(options = question.options.map { option ->
                        if (option.localId == optionLocalId) option.copy(value = text, error = null) else option
                    })
                } else {
                    question
                }
            })
        }
    }

    private fun save() {
        val currentState = viewState.value
        if (currentState.isLoading || currentState.isSaving) return
        val protocolId = currentState.protocolId?.toIntOrNull() ?: return

        var hasError = false
        val validatedQuestions = currentState.questions.map { question ->
            val questionError = if (question.name.isBlank()) "Заполните поле" else null
            if (questionError != null) hasError = true

            val validatedOptions = question.options.map { option ->
                val optionError = if (option.value.isBlank()) "Заполните поле" else null
                if (optionError != null) hasError = true
                option.copy(error = optionError)
            }
            question.copy(error = questionError, options = validatedOptions)
        }

        if (hasError) {
            setState { copy(questions = validatedQuestions) }
            return
        }

        setState { copy(isSaving = true) }
        launch {
            val items = currentState.questions.map { q ->
                TreatmentDetailResponseItem(
                    id = q.id,
                    name = q.name,
                    options = q.options.map { o ->
                        Option(
                            id = o.id,
                            value = o.value,
                            is_custom = o.isCustom
                        )
                    }
                )
            }
            when (val result = repository.updateTreatmentDetails(protocolId, items)) {
                is Result.Success -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_treatment_updated), SnackbarEvent.SnackbarType.Green))
                    setState { copy(isSaving = false) }
                    setEffect { DetailProtocolEffect.NavigateBack }
                }
                is Result.Error -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(isSaving = false) }
                    setEffect { DetailProtocolEffect.ShowError(result.message ?: "Error saving data") }
                }
                else -> {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(isSaving = false) }
                    setEffect { DetailProtocolEffect.ShowError("Unknown error") }
                }
            }
        }
    }
}